import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError, todayLocal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * EditPaymentDialog — shared across the Payments page and the Bills page's
 * BillPaymentsDialog. Edits any payment (invoice payment, bill payment, or
 * expense) via PATCH /api/payments/{pid}. Writing a non-empty `reason` is
 * mandatory — the backend rejects < 3 chars. The reason lands in the audit
 * log alongside the before/after snapshot.
 *
 * Props:
 *   payment       — the payment row to edit. When null the dialog is closed.
 *   bankAccounts  — list of { id, name, currency } to populate the Account dropdown.
 *   onClose()     — close handler
 *   onSaved()     — fired after a successful save so the parent can reload.
 */
export default function EditPaymentDialog({ payment, bankAccounts = [], onClose, onSaved }) {
    const [amount, setAmount] = useState("");
    const [paidOn, setPaidOn] = useState(todayLocal());
    const [method, setMethod] = useState("manual");
    const [accountId, setAccountId] = useState("");
    const [reference, setReference] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (payment) {
            setAmount(String(payment.amount ?? ""));
            setPaidOn(payment.paid_on || todayLocal());
            setMethod(payment.method || "manual");
            setAccountId(payment.account_id || "");
            setReference(payment.reference || "");
            setReason("");
        }
    }, [payment]);

    const submit = async () => {
        if (!reason || reason.trim().length < 3) {
            toast.error("Please provide a reason (min 3 characters) for the change");
            return;
        }
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.error("Amount must be a positive number");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/payments/${payment.id}`, {
                reason: reason.trim(),
                amount: amt,
                paid_on: paidOn,
                method,
                account_id: accountId || null,
                reference: reference || null,
            });
            toast.success("Payment updated");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const docLabel = payment?.direction === "made" || payment?.source === "bill"
        ? payment?.invoice_number || "Bill payment"
        : payment?.invoice_number || "Invoice payment";

    return (
        <Dialog open={!!payment} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit payment</DialogTitle>
                </DialogHeader>
                {payment && (
                    <div className="space-y-3">
                        <div className="rounded-2xl bg-sand-100 p-3 text-xs">
                            <div className="text-muted-foreground uppercase tracking-wider">{docLabel}</div>
                            <div className="font-medium text-ocean-700 mt-0.5">{payment.party_name || "—"}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Amount</Label>
                                <Input type="number" step="0.01" value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="mt-1.5" data-testid="edit-payment-amount" />
                            </div>
                            <div>
                                <Label>Date</Label>
                                <Input type="date" value={paidOn}
                                    onChange={(e) => setPaidOn(e.target.value)}
                                    className="mt-1.5" data-testid="edit-payment-date" />
                            </div>
                            <div>
                                <Label>Method</Label>
                                <Select value={method} onValueChange={setMethod}>
                                    <SelectTrigger className="mt-1.5" data-testid="edit-payment-method"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="manual">Cash / manual</SelectItem>
                                        <SelectItem value="bank">Bank transfer</SelectItem>
                                        <SelectItem value="card">Card</SelectItem>
                                        <SelectItem value="cheque">Cheque</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Account</Label>
                                <Select value={accountId || "__none__"} onValueChange={(v) => setAccountId(v === "__none__" ? "" : v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="edit-payment-account">
                                        <SelectValue placeholder={bankAccounts.length ? "Select account" : "No bank accounts"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— No account —</SelectItem>
                                        {bankAccounts.map((a) => (
                                            <SelectItem key={a.id} value={a.id}>
                                                {a.name}{a.currency ? ` · ${a.currency}` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Reference</Label>
                            <Input value={reference} onChange={(e) => setReference(e.target.value)}
                                className="mt-1.5" placeholder="Optional — check #, confirmation #" />
                        </div>
                        <div>
                            <Label className="text-coral-dark">Reason for change <span className="text-coral-dark">*</span></Label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g. Bank statement showed wrong amount, correcting entry"
                                className="mt-1.5"
                                rows={3}
                                data-testid="edit-payment-reason"
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                                This reason is logged in the audit trail. Required for compliance.
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || reason.trim().length < 3}
                        className="bg-ocean-700 text-white" data-testid="edit-payment-save">
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function DeletePaymentDialog({ payment, onClose, onDeleted }) {
    const [reason, setReason] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => { if (payment) setReason(""); }, [payment]);

    const submit = async () => {
        if (!reason || reason.trim().length < 3) {
            toast.error("Please provide a reason (min 3 characters) for the deletion");
            return;
        }
        setDeleting(true);
        try {
            await api.delete(`/payments/${payment.id}`, { data: { reason: reason.trim() } });
            toast.success("Payment deleted");
            onDeleted?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    };

    const isBill = payment?.direction === "made" || payment?.source === "bill";

    return (
        <Dialog open={!!payment} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete payment</DialogTitle>
                </DialogHeader>
                {payment && (
                    <div className="space-y-3">
                        <div className="rounded-2xl bg-coral/10 text-coral-dark p-3 text-xs">
                            This will remove the payment and recompute the parent {isBill ? "bill" : "invoice"} balance.
                        </div>
                        <div className="rounded-2xl bg-sand-100 p-3 text-xs">
                            <div><span className="text-muted-foreground">Doc:</span> <span className="font-mono">{payment.invoice_number || "—"}</span></div>
                            <div><span className="text-muted-foreground">Party:</span> <span className="text-ocean-700 font-medium">{payment.party_name || "—"}</span></div>
                            <div><span className="text-muted-foreground">Amount:</span> <span className="font-mono">{fmtMoney(payment.amount, payment.currency)}</span></div>
                        </div>
                        <div>
                            <Label className="text-coral-dark">Reason for deletion <span className="text-coral-dark">*</span></Label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g. Duplicate entry, vendor refund"
                                className="mt-1.5"
                                rows={3}
                                data-testid="delete-payment-reason"
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                                This reason is logged in the audit trail. Required for compliance.
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
                    <Button onClick={submit} disabled={deleting || reason.trim().length < 3}
                        className="bg-coral-dark text-white hover:bg-coral-dark/90"
                        data-testid="delete-payment-confirm">
                        {deleting ? "Deleting…" : "Delete payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
